import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout.jsx';
import { ProtectedRoute } from '../components/common/ProtectedRoute.jsx';
import { HomePage } from '../modules/home/pages/HomePage.jsx';
import { LoginPage } from '../modules/auth/pages/LoginPage.jsx';
import { RegisterPage } from '../modules/auth/pages/RegisterPage.jsx';
import { CourseListPage } from '../modules/courses/pages/CourseListPage.jsx';
import { CourseDetailPage } from '../modules/courses/pages/CourseDetailPage.jsx';
import { PharmacyToolPage } from '../modules/tools/pages/PharmacyToolPage.jsx';
import { PharmacyHealthCheckPage } from '../modules/tools/pages/PharmacyHealthCheckPage.jsx';
import { PharmacyQuizAssessmentPage } from '../modules/tools/pages/PharmacyQuizAssessmentPage.jsx';
import { PharmacySalaryToolPage } from '../modules/tools/pages/PharmacySalaryToolPage.jsx';
import { PharmacyPromotionPricingToolPage } from '../modules/tools/pages/PharmacyPromotionPricingToolPage.jsx';
import { PharmacySalesCampaignToolPage } from '../modules/tools/pages/PharmacySalesCampaignToolPage.jsx';
import { ToolsHubPage } from '../modules/tools/pages/ToolsHubPage.jsx';
import { ToolAccessGuard } from '../modules/tools/components/ToolAccessGuard.jsx';
import { VideoBatchToolPage } from '../modules/tools/pages/VideoBatchToolPage.jsx';
import { HEALTH_HAND_VIDEO_TOOL } from '../modules/tools/data/healthHandVideoToolContent.js';
import { QA_VIDEO_SKILL_TOOL } from '../modules/tools/data/qaVideoSkillContent.js';
import { STORY_VIDEO_SKILL_TOOL } from '../modules/tools/data/storyVideoSkillContent.js';
import { COMPARE_VIDEO_TOOL } from '../modules/tools/data/compareVideoToolContent.js';
import { EXPERT_VIDEO_SKILL_TOOL } from '../modules/tools/data/expertVideoSkillContent.js';
import { SkillGuidePage } from '../modules/tools/pages/SkillGuidePage.jsx';
import { pharmacyToolApi } from '../api/pharmacy-tool.api.js';
import { LessonPlayerPage } from '../modules/learning/pages/LessonPlayerPage.jsx';
import { QuizPage } from '../modules/quiz/pages/QuizPage.jsx';
import { LearnerDashboardPage } from '../modules/dashboard/pages/LearnerDashboardPage.jsx';
import { ProfilePage } from '../modules/profile/pages/ProfilePage.jsx';
import { CheckoutPage } from '../modules/payment/pages/CheckoutPage.jsx';
import { AdminDashboardPage } from '../modules/admin/pages/AdminDashboardPage.jsx';
import { ErrorState } from '../components/common/ErrorState.jsx';

export function AppRoutes() {
  return (
    <Routes>
      {/* Trang quản trị: khung riêng (thanh bên + thanh trên), không dùng navbar, chân trang của MainLayout */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRoles={['ADMIN', 'INSTRUCTOR']}>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Main Layout Routes */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/courses" element={<CourseListPage />} />
        <Route path="/courses/:idOrSlug" element={<CourseDetailPage />} />
        <Route path="/tools" element={<ToolsHubPage />} />
        {/* ToolAccessGuard: công cụ thu phí (back-end constants/tool-access.constant.js) mà chưa có gói thì hiện màn hình mua gói; có `preview` thì vào xem thử được, làm chi tiết mới hiện modal mua */}
        <Route path="/tools/quan-tri-thuc-chien" element={<ToolAccessGuard slug="quan-tri-thuc-chien"><PharmacyToolPage /></ToolAccessGuard>} />
        <Route path="/tools/do-luong-suc-khoe" element={<ToolAccessGuard slug="do-luong-suc-khoe"><PharmacyHealthCheckPage /></ToolAccessGuard>} />
        <Route path="/tools/radar-suc-khoe" element={<Navigate to="/tools/do-luong-suc-khoe" replace />} />
        <Route path="/tools/trac-nghiem-thuc-chien" element={<ToolAccessGuard slug="trac-nghiem-thuc-chien"><PharmacyQuizAssessmentPage /></ToolAccessGuard>} />
        <Route path="/tools/trac-nghiem-kien-thuc" element={<Navigate to="/tools/trac-nghiem-thuc-chien" replace />} />
        <Route path="/tools/co-che-luong-thuong" element={<ToolAccessGuard slug="co-che-luong-thuong" preview><PharmacySalaryToolPage /></ToolAccessGuard>} />
        <Route path="/tools/luong-thuong" element={<Navigate to="/tools/co-che-luong-thuong" replace />} />
        <Route path="/tools/chuong-trinh-ban-hang" element={<ToolAccessGuard slug="chuong-trinh-ban-hang" preview><PharmacySalesCampaignToolPage /></ToolAccessGuard>} />
        {/* Trang giới thiệu + hướng dẫn cài công cụ tạo video hàng loạt (tải zip cần đăng nhập).
            Không bọc ToolAccessGuard: khách chưa mua vẫn vào xem trang, video mẫu; nút tải tự đổi thành nút mua (API tải vẫn khóa ở máy chủ) */}
        <Route path="/tools/tao-video-hang-loat" element={<VideoBatchToolPage />} />
        {/* Cùng trang, nội dung công cụ tạo video sức khỏe tay chỉ. Chưa phát hành (published: false) thì chỉ ADMIN mở được */}
        <Route
          path={`/tools/${HEALTH_HAND_VIDEO_TOOL.slug}`}
          element={
            HEALTH_HAND_VIDEO_TOOL.published ? (
              <VideoBatchToolPage tool={HEALTH_HAND_VIDEO_TOOL} getDownload={pharmacyToolApi.getHealthVideoToolDownload} />
            ) : (
              <ProtectedRoute requiredRoles={['ADMIN']}>
                <VideoBatchToolPage tool={HEALTH_HAND_VIDEO_TOOL} getDownload={pharmacyToolApi.getHealthVideoToolDownload} />
              </ProtectedRoute>
            )
          }
        />
        {/* Bộ skill video (ChatGPT + Google Flow): không bọc ToolAccessGuard để khách chưa mua vẫn xem video mẫu; nút tải tự đổi thành nút mua */}
        <Route
          path={`/tools/${QA_VIDEO_SKILL_TOOL.slug}`}
          element={<VideoBatchToolPage tool={QA_VIDEO_SKILL_TOOL} getDownload={pharmacyToolApi.getQaVideoSkillDownload} />}
        />
        <Route
          path={`/tools/${STORY_VIDEO_SKILL_TOOL.slug}`}
          element={<VideoBatchToolPage tool={STORY_VIDEO_SKILL_TOOL} getDownload={pharmacyToolApi.getStoryVideoSkillDownload} />}
        />
        {/* Plugin video dược sĩ / chuyên gia: bấm tải mở thêm trang hướng dẫn (công khai) ở tab mới */}
        <Route
          path={`/tools/${EXPERT_VIDEO_SKILL_TOOL.slug}`}
          element={
            <VideoBatchToolPage
              tool={EXPERT_VIDEO_SKILL_TOOL}
              getDownload={pharmacyToolApi.getExpertVideoSkillDownload}
              getGuideDownload={pharmacyToolApi.getExpertVideoGuideDownload}
            />
          }
        />
        {/* Video so sánh hàng loạt: file trên Google Drive, mua xong bấm tải thì mở thư mục Drive */}
        <Route
          path={`/tools/${COMPARE_VIDEO_TOOL.slug}`}
          element={<VideoBatchToolPage tool={COMPARE_VIDEO_TOOL} getDownload={pharmacyToolApi.getCompareVideoToolDownload} />}
        />
        <Route path={EXPERT_VIDEO_SKILL_TOOL.guidePath} element={<SkillGuidePage tool={EXPERT_VIDEO_SKILL_TOOL} />} />
        <Route path="/tools/len-chuong-trinh-ban-hang" element={<Navigate to="/tools/chuong-trinh-ban-hang" replace />} />
        <Route
          path="/tools/gia-nhap-that"
          element={
            <ProtectedRoute requiredRoles={['ADMIN']}>
              <ToolAccessGuard slug="gia-nhap-that">
                <PharmacyPromotionPricingToolPage />
              </ToolAccessGuard>
            </ProtectedRoute>
          }
        />
        <Route path="/tools/chia-gia-khuyen-mai" element={<Navigate to="/tools/gia-nhap-that" replace />} />
        <Route path="/tools/khuyen-mai-ncc" element={<Navigate to="/tools/gia-nhap-that" replace />} />
        <Route path="/tool" element={<ToolAccessGuard slug="quan-tri-thuc-chien"><PharmacyToolPage /></ToolAccessGuard>} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        
        {/* Protected Dashboard Học Viên */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <LearnerDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Hồ sơ cá nhân: xem, sửa thông tin, đổi mật khẩu */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Thanh toán QR chuyển khoản (SePay) - yêu cầu đăng nhập. ?returnTo=/tools/... để quay lại công cụ sau khi trả tiền */}
        <Route
          path="/checkout/:planId"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route path="/checkout/result" element={<Navigate to="/dashboard" replace />} />

        {/* 404: route con của MainLayout để hiện trong <Outlet /> cùng thanh điều hướng, chân trang (HT-06 F-11) */}
        <Route
          path="*"
          element={
            <ErrorState
              status="404"
              title="404 - Không Tìm Thấy Trang"
              subTitle="Đường dẫn bạn yêu cầu không tồn tại hoặc đã được chuyển dời."
            />
          }
        />
      </Route>

      {/* Fullscreen Learning Player (Protected) */}
      <Route
        path="/learn/:courseId/lessons/:lessonId"
        element={
          <ProtectedRoute>
            <LessonPlayerPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/learn/paths/:pathId/lessons/:lessonId"
        element={
          <ProtectedRoute>
            <LessonPlayerPage />
          </ProtectedRoute>
        }
      />

      {/* Fullscreen Quiz Page (Protected) */}
      <Route
        path="/quiz/:quizId"
        element={
          <ProtectedRoute>
            <QuizPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
